import React, { useEffect, useState, useCallback } from "react";
import { Calendar, MapPin, Award, Users, Plus, Loader2, Trash2, Check, Search, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, formatDateTime, formatDateShort } from "@/lib/api";
import { useAuth, useRequireAuth } from "@/context/AuthContext";
import { MedLyncVerifiedBadge } from "@/components/Badges";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

const CATS = ["All", "Conference", "Workshop", "CME", "Camp"];

function CreateEventDialog({ open, onOpenChange, onCreated }) {
  const [f, setF] = useState({ title: "", description: "", category: "Conference", venue: "", city: "Ahmedabad", start_at: "", end_at: "", organizer: "", cme_credits: "", capacity: "" });
  const [cover, setCover] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData(); form.append("file", file);
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setCover(data.url); toast.success("Cover added");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!f.title.trim() || !f.description.trim() || !f.start_at) { toast.info("Add title, description and start date"); return; }
    setBusy(true);
    try {
      await api.post("/events", {
        ...f, start_at: new Date(f.start_at).toISOString(),
        end_at: f.end_at ? new Date(f.end_at).toISOString() : null,
        cme_credits: f.cme_credits || null, capacity: f.capacity ? parseInt(f.capacity) : null,
        cover_url: cover || null,
      });
      toast.success("Event created"); onOpenChange(false); onCreated();
      setF({ title: "", description: "", category: "Conference", venue: "", city: "Ahmedabad", start_at: "", end_at: "", organizer: "", cme_credits: "", capacity: "" }); setCover("");
    } catch { toast.error("Could not create event"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto" data-testid="create-event-modal">
        <DialogHeader><DialogTitle className="font-heading">Add an Event</DialogTitle><DialogDescription className="sr-only">Create a healthcare event listing.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Input data-testid="event-title-input" value={f.title} onChange={set("title")} placeholder="Event title" />
          <Textarea data-testid="event-desc-input" value={f.description} onChange={set("description")} placeholder="Description" className="min-h-[90px]" />
          <div className="grid grid-cols-2 gap-2">
            <Select value={f.category} onValueChange={(v) => setF((p) => ({ ...p, category: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CATS.filter((c) => c !== "All").map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={f.cme_credits} onChange={set("cme_credits")} placeholder="CME credits" />
          </div>
          <Input value={f.venue} onChange={set("venue")} placeholder="Venue" />
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-[11px] text-slate-500">Starts</Label><Input data-testid="event-start-input" type="datetime-local" value={f.start_at} onChange={set("start_at")} /></div>
            <div><Label className="text-[11px] text-slate-500">Ends</Label><Input type="datetime-local" value={f.end_at} onChange={set("end_at")} /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input value={f.organizer} onChange={set("organizer")} placeholder="Organizer" />
            <Input value={f.capacity} onChange={set("capacity")} type="number" placeholder="Capacity (opt)" />
          </div>
          {cover && <div className="relative"><img src={fileUrl(cover)} alt="" className="h-28 w-full rounded-xl object-cover" /><button onClick={() => setCover("")} className="absolute right-2 top-2 rounded-full bg-black/60 p-1 text-white"><X className="h-3 w-3" /></button></div>}
          <div className="flex items-center justify-between">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-500 hover:text-navy">{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} Cover<input type="file" accept="image/*" className="hidden" onChange={uploadCover} /></label>
            <Button data-testid="event-submit-button" onClick={submit} disabled={busy} className="bg-medorange hover:bg-medorange-hover">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create event"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Events() {
  const { user } = useAuth();
  const requireAuth = useRequireAuth();
  const [when, setWhen] = useState("upcoming");
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const [events, setEvents] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    const params = { when };
    if (cat !== "All") params.category = cat;
    if (q) params.q = q;
    const { data } = await api.get("/events", { params });
    setEvents(data);
  }, [when, cat, q]);
  useEffect(() => { load(); }, [load]);

  const register = (e) => requireAuth(async () => {
    try {
      const { data } = await api.post(`/events/${e.id}/register`);
      setEvents((list) => list.map((x) => (x.id === e.id ? data : x)));
      toast.success(data.registered ? "You're registered!" : "Registration cancelled");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not register"); }
  });
  const verify = async (e) => { const { data } = await api.patch(`/events/${e.id}/verify`); setEvents((l) => l.map((x) => (x.id === e.id ? { ...x, medlync_verified: data.medlync_verified } : x))); };
  const remove = async (e) => { await api.delete(`/events/${e.id}`); toast.success("Deleted"); load(); };

  const isAdmin = user && user.role === "admin";

  return (
    <div className="px-4 py-4 pb-8" data-testid="events-page">
      <div className="mb-3 flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-medorange-light text-medorange"><Calendar className="h-5 w-5" /></span>
        <div className="flex-1"><h1 className="font-heading text-xl font-extrabold text-slate-900">Events</h1><p className="text-xs text-slate-500">CME, conferences, workshops & camps</p></div>
        <Button data-testid="add-event-button" onClick={() => requireAuth(() => setOpen(true))} size="sm" className="gap-1 bg-navy hover:bg-navy-hover"><Plus className="h-4 w-4" /> Add</Button>
      </div>

      <Tabs value={when} onValueChange={setWhen} className="mb-3">
        <TabsList className="w-full bg-slate-100">
          <TabsTrigger value="upcoming" data-testid="events-tab-upcoming" className="flex-1 data-[state=active]:text-medorange">Upcoming</TabsTrigger>
          <TabsTrigger value="past" data-testid="events-tab-past" className="flex-1 data-[state=active]:text-medorange">Past</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="no-scrollbar mb-3 flex gap-2 overflow-x-auto">
        {CATS.map((c) => (
          <button key={c} onClick={() => setCat(c)} className={cn("shrink-0 rounded-full px-4 py-1.5 text-xs font-semibold transition-colors", cat === c ? "bg-medorange text-white" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-100")}>{c}</button>
        ))}
      </div>

      <div className="mb-4 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5">
        <Search className="h-4 w-4 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search events…" className="flex-1 bg-transparent text-sm outline-none" />
      </div>

      <div className="space-y-4">
        {events === null && [1, 2].map((i) => <Skeleton key={i} className="h-40 w-full rounded-2xl" />)}
        {events && events.length === 0 && <p className="py-10 text-center text-sm text-slate-400">No {when} events.</p>}
        {events && events.map((e) => {
          const d = formatDateShort(e.start_at);
          return (
            <div key={e.id} data-testid="event-card" className="ml-rise overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              {e.cover_url && <img src={fileUrl(e.cover_url)} alt="" className="h-28 w-full object-cover" />}
              <div className="p-4">
                <div className="flex gap-3">
                  <div className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-xl bg-navy text-white">
                    <span className="font-heading text-lg font-extrabold leading-none">{d.day}</span>
                    <span className="text-[10px] font-bold">{d.mon}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-bold uppercase text-indigo-700">{e.category}</span>
                      {e.cme_credits && <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700"><Award className="h-3 w-3" /> {e.cme_credits} CME</span>}
                      {e.medlync_verified && <MedLyncVerifiedBadge compact />}
                    </div>
                    <h3 className="mt-1 font-heading text-base font-bold leading-snug text-slate-900">{e.title}</h3>
                    <p className="text-xs text-slate-500">{formatDateTime(e.start_at)}</p>
                  </div>
                  {(isAdmin || (user && user.id === e.created_by)) && <button onClick={() => remove(e)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}
                </div>
                <p className="mt-2 line-clamp-2 text-sm text-slate-600">{e.description}</p>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                  {e.venue && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {e.venue}</span>}
                  <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {e.registration_count}{e.capacity ? `/${e.capacity}` : ""} going</span>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  {when === "upcoming" && (
                    <Button onClick={() => register(e)} data-testid={`event-register-${e.id}`} className={cn("flex-1", e.registered ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-medorange text-white hover:bg-medorange-hover")}>
                      {e.registered ? <><Check className="mr-1 h-4 w-4" /> Registered</> : "Register"}
                    </Button>
                  )}
                  {isAdmin && <Button variant="outline" size="sm" onClick={() => verify(e)}>{e.medlync_verified ? "Unverify" : "Verify"}</Button>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <CreateEventDialog open={open} onOpenChange={setOpen} onCreated={load} />
    </div>
  );
}
